import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { submitReview, updateReview } from '../api';

// ── Info Tooltip ─────────────────────────────────────────────
// Uses position:fixed so the popup escapes every stacking context (modal, labels, etc.)
function InfoTip({ text, position = 'top' }) {
  const [coords, setCoords] = useState(null);
  const triggerRef = React.useRef(null);
  const W = 180; // tooltip width px

  const show = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    let style = {};
    if (position === 'top')    style = { bottom: window.innerHeight - r.top + 10, left: cx - W / 2 };
    if (position === 'bottom') style = { top: r.bottom + 10,                       left: cx - W / 2 };
    if (position === 'left')   style = { top: cy - 28,                             right: window.innerWidth - r.left + 10 };
    setCoords(style);
  };

  const hide = () => setCoords(null);
  const visible = !!coords;

  const tooltip = visible ? ReactDOM.createPortal(
    <div style={{
      position: 'fixed', zIndex: 99999, width: W,
      background: 'rgba(30,41,59,0.96)', color: '#fff', borderRadius: 8,
      padding: '8px 12px', fontSize: 11, fontWeight: 500, lineHeight: 1.5,
      textAlign: 'center', pointerEvents: 'none',
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.1)',
      ...coords,
    }}>
      {text}
    </div>,
    document.body
  ) : null;

  return (
    <span
      ref={triggerRef}
      style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, verticalAlign: 'middle', cursor: 'help' }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onClick={(e) => { e.stopPropagation(); visible ? hide() : show(); }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 13, height: 13, borderRadius: '50%',
        background: visible ? 'var(--blue)' : 'var(--surface2)',
        color: visible ? '#fff' : 'var(--text3)',
        border: `1px solid ${visible ? 'var(--blue)' : 'var(--border)'}`,
        fontSize: 9, fontWeight: 700, transition: 'all 0.2s ease',
      }}>?</span>
      {tooltip}
    </span>
  );
}

const StarRating = ({ label, value, onChange }) => {
  const [hover, setHover] = useState(null);
  return (
    <div className="star-rating-item" style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.5 }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(null)}
            onClick={() => onChange(star)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 18,
              color: (hover || value) >= star ? '#FFB800' : 'var(--border)',
              transition: 'all 0.1s',
              transform: hover === star ? 'scale(1.1)' : 'scale(1)',
            }}
          >
            ★
          </button>
        ))}
        <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 700, color: 'var(--text2)', alignSelf: 'center' }}>
          {value}/5
        </span>
      </div>
    </div>
  );
};

function ComboBox({ name, value, onChange, options, placeholder, inputStyle, required = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(value.toLowerCase()));

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        required={required}
        autoComplete="off"
        name={name}
        value={value}
        onChange={(e) => { onChange(e); setIsOpen(true); }}
        onFocus={() => setIsOpen(true)}
        style={{ ...inputStyle, paddingRight: 30 }}
        placeholder={placeholder}
      />
      <div onClick={() => setIsOpen(!isOpen)} style={{ position: 'absolute', right: 12, top: 12, fontSize: 10, cursor: 'pointer', color: 'var(--text3)' }}>
        ▼
      </div>
      {isOpen && filtered.length > 0 && (
        <div className="no-scrollbar" style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, maxHeight: 300, overflowY: 'auto',
        }}>
          {filtered.map(opt => (
            <div
              key={opt}
              onClick={() => {
                onChange({ target: { name, value: opt } });
                setIsOpen(false);
              }}
              style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border2)', color: 'var(--text)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const COMPANIES = [];

const ROLES = [
  'Academic Specialist', 'Actuarial Specialist', 'Advertising Operations Analyst', 'AI Data Analyst',
  'AI Engineer', 'AI Researcher', 'Applied Scientist (Machine Learning)', 'Automation Specialist',
  'Bilingual Support Specialist', 'Business & Technology Delivery Associate', 'Business Analyst',
  'Business Development Manager', 'Cybersecurity Analyst', 'Claims Specialist', 'Clinical Data Analyst',
  'Clinical Support Specialist', 'Cloud Architect', 'Community Support Lead', 'Compliance Specialist',
  'Configuration Analyst', 'Content Marketer', 'Content Moderator', 'Content Reviewer',
  'Content Strategist', 'Content Writer', 'Customer Service Manager', 'Customer Service Representative',
  'Customer Success Manager', 'Customer Support', 'Customer Support Specialist', 'Data Analyst',
  'Data Engineer', 'Data Scientist', 'Demand Generation Lead', 'Design Engineer',
  'Digital Marketer', 'Digital Marketing Specialist', 'Digital Solution Architect', 'Financial Analyst',
  'Finance Analyst', 'Frontend Developer', 'Full Stack Developer', 'GTM Engineer',
  'Ground Segment Engineer', 'Growth Marketing Lead', 'HR Executive', 'HR Generalist',
  'HR Operations', 'Identity Management Specialist', 'Investment Research Analyst', 'Java Developer',
  'Learning & Development Specialist', 'Marketing Strategist', 'Medical Coder', 'MERN Stack Developer',
  'ML Engineer', 'Mobile App Developer', 'Network Specialist', 'Operations Associate',
  'Operations Lead', 'Operations Manager', 'Payment Software Engineer', 'Performance Marketing Specialist',
  'Process Associate', 'Product Designer', 'Product Manager', 'Product Marketer',
  'Project Coordinator', 'Project Manager', 'PropTech Consultant', 'QA Automation Engineer',
  'QA Automation Specialist', 'QA Engineer', 'R&D Engineer', 'Recruiter',
  'Remote Health Advocate', 'Research Consultant', 'Risk Analyst', 'SAP Functional Consultant',
  'Sales Associate', 'Sales Representative', 'Search Engine Evaluator', 'SecOps Engineer',
  'Security Governance Manager', 'Senior Backend Developer', 'Senior Clinical Quality Analyst',
  'Software Developer', 'Software Development Engineer (SDE)', 'Software Engineer', 'SRE / DevOps',
  'Strategy Consultant', 'Success Manager', 'Support Engineer', 'Technical Support',
  'Technical Support Engineer', 'UI/UX Designer', 'UI/UX Lead', 'UI Designer',
  'Virtual Customer Service', 'Virtual Customer Service Associate'
].sort();

const LOCATIONS = [
  'Bengaluru', 'Hyderabad', 'Pune', 'Chennai', 'Gurugram', 'Noida',
  'Mumbai', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Chandigarh', 'Kochi',
  'Bhubaneswar', 'Lucknow', 'Indore', 'Nagpur', 'Coimbatore',
  'Visakhapatnam', 'Mysuru', 'Surat', 'Thiruvananthapuram', 'Madurai',
  'Vijayawada', 'Warangal', 'Remote',
];

const ACCEPTED_LOCATIONS = new Set(LOCATIONS.map(l => l.toLowerCase()));

function isAcceptedLocation(loc) {
  return ACCEPTED_LOCATIONS.has(loc.trim().toLowerCase());
}





const EMPTY_FORM = {
  companyName: '', role: '', location: '', status: 'offer',
  commRating: 4, diffRating: 3, fairRating: 5, description: '',
  rounds: 1, timeline: '', salaryAmount: '', interviewQuestion: '',
};

export default function Modal({ isOpen, onClose, onSuccess, initialData }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSalaryPopup, setShowSalaryPopup] = useState(false);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [dynCompanies, setDynCompanies] = useState(COMPANIES);
  const [dynRoles, setDynRoles] = useState(ROLES);
  const errorRef = React.useRef(null);

  React.useEffect(() => {
    fetch('/data/companies.json').then(r => r.json()).then(setDynCompanies).catch(() => {});
    fetch('/data/roles.json').then(r => r.json()).then(setDynRoles).catch(() => {});
  }, []);
  const kahaniRef = React.useRef(null);

  const isEditing = !!initialData;

  React.useEffect(() => {
    if (isOpen) {
      setError('');
      if (initialData) {
        setFormData({
          companyName: initialData.companyName || initialData.company || '',
          role: initialData.role || '',
          location: initialData.location || '',
          status: initialData.status || initialData.verdict || 'offer',
          commRating: initialData.ratings?.communication || 4,
          diffRating: initialData.ratings?.difficulty || 3,
          fairRating: initialData.ratings?.fairness || 5,
          description: initialData.description || initialData.excerpt || '',
          rounds: initialData.rounds || 1,
          timeline: initialData.timeline || '',
          salaryAmount: initialData.salary?.amount ? (initialData.salary.amount / 100000).toFixed(1) : '',
          interviewQuestion: initialData.interviewQuestion || '',
        });
      } else {
        setFormData(EMPTY_FORM);
      }
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const buildPayload = () => {
    const payload = {
      companyName: formData.companyName.trim(),
      role: formData.role.trim(),
      location: formData.location.trim(),
      description: formData.description.trim(),
      isAnonymous: false,
      status: formData.status || 'offer',
      rounds: formData.rounds ? Number(formData.rounds) : 1,
    };
    
    if (formData.timeline) payload.timeline = formData.timeline;
    if (formData.salaryAmount) {
      const lpa = parseFloat(parseFloat(formData.salaryAmount).toFixed(1));
      payload.salary = { amount: Math.round(lpa * 100000), currency: 'INR', period: 'annual' };
    }
    
    // Only send ratings if valid (1-5) to avoid backend validation errors on 0 values
    const ratings = {};
    if (formData.commRating > 0) ratings.communication = Number(formData.commRating);
    if (formData.diffRating > 0) ratings.difficulty = Number(formData.diffRating);
    if (formData.fairRating > 0) ratings.fairness = Number(formData.fairRating);
    
    if (Object.keys(ratings).length > 0) {
      payload.ratings = ratings;
    }
    
    if (formData.interviewQuestion) payload.interviewQuestion = formData.interviewQuestion;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName.trim()) { 
      setError('Company name is required.'); 
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return; 
    }
    if (!formData.role.trim()) { 
      setError('Role/Designation is required.'); 
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return; 
    }
    if (!formData.location.trim()) {
      setError('Location is required.');
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    if (!isAcceptedLocation(formData.location)) {
      setShowLocationPopup(true);
      return;
    }
    if (!formData.description.trim()) {
      setError('Please write your kahani.');
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      kahaniRef.current?.focus();
      return;
    }
    if (formData.salaryAmount) {
      const lpa = parseFloat(formData.salaryAmount);
      const isValidFormat = /^\d{1,3}(\.\d)?$/.test(formData.salaryAmount.trim());
      if (!isValidFormat || isNaN(lpa) || lpa < 3 || lpa > 100) {
        setShowSalaryPopup(true);
        return;
      }
    }
    setLoading(true);
    setError('');

    try {
      let result;
      const payload = buildPayload();
      
      if (isEditing) {
        const id = initialData._id || initialData.id;
        result = await updateReview(id, payload);
      } else {
        result = await submitReview(payload);
      }
      
      onSuccess(result.review);
      onClose();
    } catch (err) {
      console.error('Submission failed:', err);
      let msg = err.message || 'Failed to share your kahani. Please try again.';
      
      // Access structured data from our updated apiFetch error
      if (err.data) {
        if (err.data.errors && Array.isArray(err.data.errors)) {
          msg = err.data.errors.map(e => e.msg).join('. ');
        } else if (err.data.error) {
          msg = err.data.error;
        }
      }
      
      setError(msg);
      // Scroll to error so user sees it on mobile
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const inputContainerStyle = { marginBottom: 14, position: 'relative' };
  const inputLabelStyle = {
    position: 'absolute', top: -7, left: 10, background: 'var(--surface)',
    padding: '0 4px', fontSize: 9, fontWeight: 800, color: 'var(--blue)',
    textTransform: 'uppercase', letterSpacing: 0.5, zIndex: 1,
  };
  const inputStyle = {
    width: '100%', padding: '7px 14px', borderRadius: 8,
    border: '1px solid var(--border)', background: 'var(--surface)',
    fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--text)',
    outline: 'none', transition: 'all 0.2s', boxSizing: 'border-box',
  };

  return (
    <div className="modal-backdrop modal-overlay" style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(100,116,139,0.45)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      padding: 16,
    }}>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      <div className="modal-box modal-box-bg" style={{
        background: 'var(--bg2)', borderRadius: 24, width: '100%', maxWidth: 900,
        padding: 0, boxShadow: '0 30px 60px -12px rgba(0,0,0,0.3)',
        position: 'relative', maxHeight: '90vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div className="modal-header-bar" style={{ padding: '16px 28px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 20, margin: 0, color: 'var(--blue)', fontWeight: 900, letterSpacing: 1 }}>
            KAHANI
          </h2>
          <button onClick={onClose} className="modal-close-btn" style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, cursor: 'pointer', color: 'var(--text3)',
          }}>×</button>
        </div>

        {error && (
          <div ref={errorRef} style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 28px', fontSize: 13, fontWeight: 600, borderBottom: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div
            style={{ display: 'flex', flex: 1, overflow: 'hidden' }}
            className="modal-form-grid"
          >
            {/* Left column: inputs + ratings */}
            <div className="modal-left-col" style={{ flex: '0 0 min(350px, 100%)', maxWidth: 350, display: 'flex', flexDirection: 'column', background: 'var(--surface)', overflowY: 'auto', overflowX: 'hidden', paddingTop: 5 }}>
              {/* Panel 1: Input fields */}
              <div className="no-scrollbar modal-inputs-panel" style={{ padding: '10px 20px 0' }}>
                <div style={inputContainerStyle}>
                  <label style={inputLabelStyle}>Company <InfoTip text="Name of the company where you interviewed" position="bottom" /></label>
                  <ComboBox name="companyName" value={formData.companyName} onChange={handleChange} inputStyle={inputStyle}
                    placeholder="Select or type company" options={dynCompanies} required={true} />
                </div>

                <div style={inputContainerStyle}>
                  <label style={inputLabelStyle}>Role <InfoTip text="The position/designation you applied for" /></label>
                  <ComboBox name="role" value={formData.role} onChange={handleChange} inputStyle={inputStyle}
                    placeholder="Select or type role" options={dynRoles} required={true} />
                </div>

                <div style={inputContainerStyle}>
                  <label style={inputLabelStyle}>Location <InfoTip text="City where the role/interview was based" /></label>
                  <ComboBox name="location" value={formData.location} onChange={handleChange} inputStyle={inputStyle}
                    placeholder="Select or type location" options={LOCATIONS} required={true} />
                </div>

                <div className="modal-rounds-field" style={inputContainerStyle}>
                  <label style={inputLabelStyle}>Rounds <InfoTip text="Total number of interview rounds (including HR)" /></label>
                  <input type="number" min="1" max="15" name="rounds" value={formData.rounds} onChange={handleChange} style={inputStyle} placeholder="1" />
                </div>

                <div style={inputContainerStyle}>
                  <label style={inputLabelStyle}>Status <InfoTip text="Final outcome of your interview process" /></label>
                  <select name="status" value={formData.status} onChange={handleChange} style={inputStyle}>
                    <option value="offer">Received Offer ✅</option>
                    <option value="rejected">Rejected ❌</option>
                    <option value="ghosted">Ghosted 👻</option>
                    <option value="ongoing">In Progress ⏳</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={inputContainerStyle}>
                    <label style={inputLabelStyle}>Timeline <InfoTip text="How long the entire process took (e.g. 2 weeks)" /></label>
                    <input name="timeline" value={formData.timeline} onChange={handleChange} style={inputStyle} placeholder="e.g. 2 weeks" />
                  </div>
                  <div style={inputContainerStyle}>
                    <label style={inputLabelStyle}>Salary (LPA) <InfoTip text="Offered CTC in Lakhs Per Annum" position="left" /></label>
                    <input
                      name="salaryAmount"
                      value={formData.salaryAmount}
                      onChange={handleChange}
                      placeholder="e.g. 18.5"
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>

              {/* Panel 3: Star ratings */}
              <div className="modal-ratings-panel" style={{ padding: '10px 20px 20px', background: 'var(--surface)' }}>
                <StarRating label="Communication" value={formData.commRating} onChange={v => setFormData({ ...formData, commRating: v })} />
                <StarRating label="Difficulty" value={formData.diffRating} onChange={v => setFormData({ ...formData, diffRating: v })} />
                <StarRating label="Fairness" value={formData.fairRating} onChange={v => setFormData({ ...formData, fairRating: v })} />
              </div>
            </div>

            {/* Right column: kahani + interview question */}
            <div className="modal-right-col" style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--surface)', overflow: 'hidden' }}>
              {/* Panel 2: Kahani textarea */}
              <div className="modal-kahani-panel" style={{ flex: 1, padding: '10px 20px 20px', display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', marginBottom: 8 }}>
                  KAHANI
                </label>
                <textarea
                  ref={kahaniRef}
                  className="kahani-textarea"
                  name="description" value={formData.description} onChange={handleChange}
                  style={{
                    flex: 1, minHeight: 360, padding: 20, borderRadius: 16, border: '1px solid var(--border)',
                    background: 'var(--surface2)', fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--text)',
                    outline: 'none', resize: 'none', lineHeight: 1.6,
                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
                  }}
                  placeholder="WRITE YOUR KAHANI.."
                />
              </div>

              {/* Panel 4: Key Interview Question */}
              <div className="modal-question-panel" style={{ padding: '0 20px 20px' }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  Key Interview Question <InfoTip text="The most memorable/important question asked during your interview" />
                </label>
                <input
                  name="interviewQuestion"
                  value={formData.interviewQuestion}
                  onChange={handleChange}
                  onBlur={e => {
                    const v = e.target.value.trim();
                    if (v && !v.endsWith('?')) {
                      setFormData(f => ({ ...f, interviewQuestion: v + '?' }));
                    }
                  }}
                  style={inputStyle}
                  placeholder="e.g. How would you design a rate limiter?"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '16px 28px', background: 'var(--surface)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'center' }}>
            <button
              disabled={loading}
              type="submit"
              className="modal-submit-btn"
              style={{
                background: 'var(--blue)', color: '#fff', border: 'none', padding: '12px 80px',
                borderRadius: 12, fontSize: 15, fontWeight: 800, fontFamily: 'var(--sans)',
                cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1,
                transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(14,165,233,0.3)',
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = '#0284c7'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = 'var(--blue)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
            >
              {loading ? (isEditing ? 'Saving...' : 'Sharing...') : (isEditing ? 'Save Changes' : 'Share Kahani')}
            </button>
          </div>
        </form>
      </div>

      {/* Location not in India popup */}
      {showLocationPopup && ReactDOM.createPortal(
        <div
          onClick={() => setShowLocationPopup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 16, padding: '28px 24px',
              maxWidth: 340, width: '100%', textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: '1px solid var(--border)',
              animation: 'fadeUp 0.2s ease-out',
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>🇮🇳</div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 900, margin: '0 0 8px', color: 'var(--text)' }}>
              Location not in India
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 20px', lineHeight: 1.6 }}>
              Please select from the accepted cities: Bengaluru, Hyderabad, Pune, Chennai, Gurugram, Noida, Mumbai, Kolkata, Ahmedabad, Jaipur, Chandigarh, Kochi, Bhubaneswar, Lucknow, Indore, Nagpur, Coimbatore, Visakhapatnam, Mysuru, Surat, Thiruvananthapuram, Madurai, Vijayawada, Warangal or <strong>Remote</strong>.
            </p>
            <button
              onClick={() => setShowLocationPopup(false)}
              style={{
                background: 'var(--blue)', color: '#fff', border: 'none',
                padding: '11px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--sans)', width: '100%',
              }}
            >
              Fix Location
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Salary out-of-bounds popup */}
      {showSalaryPopup && ReactDOM.createPortal(
        <div
          onClick={() => setShowSalaryPopup(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--surface)', borderRadius: 16, padding: '28px 24px',
              maxWidth: 340, width: '100%', textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)', border: '1px solid var(--border)',
              animation: 'fadeUp 0.2s ease-out',
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 900, margin: '0 0 8px', color: 'var(--text)' }}>
              Salary out of bounds
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', margin: '0 0 6px', lineHeight: 1.6 }}>
              Please enter a value between <strong>3</strong> and <strong>100</strong> LPA.
            </p>
            <p style={{ fontSize: 12, color: 'var(--text3)', margin: '0 0 20px' }}>
              e.g. <code style={{ background: 'var(--surface2)', padding: '1px 6px', borderRadius: 4 }}>18.5</code> or <code style={{ background: 'var(--surface2)', padding: '1px 6px', borderRadius: 4 }}>42.0</code>
            </p>
            <button
              onClick={() => setShowSalaryPopup(false)}
              style={{
                background: 'var(--blue)', color: '#fff', border: 'none',
                padding: '11px 24px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--sans)', width: '100%',
              }}
            >
              Fix Salary
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
